import { IHighlightBadgeProperties } from '@vfde-brix/ws10/highlight-badge';
import {
    CmsPlaceholder,
    FeeText,
} from '../../enum';
import {
    JarvisPriceMap,
    JarvisProduct,
    JarvisTvProduct,
} from '../../interface';
import { getCMSConfig } from '../cms';
import {
    createDurationText,
    createLegalDiscountText,
    createStartBonusText,
} from '../formatter';
import { isNotEmptyString } from '../typeGuard';
import {
    NO_PATTERN_BUSINESS_LOGIC,
    Price,
} from '@vfde-brix/ws10/core';
import { IOfferSummaryCardAdditionalPrice } from '@vfde-brix/ws10/offer-summary-card';

/**
 * Gets the props to be updated on the OfferSummaryCard highlight badge
 *
 * @param isContentEnabled - If tariffs are available or not
 * @param startBonus - Start bonus if user has any
 */
export const getHighlightBadgeProps = (
    isContentEnabled: boolean,
    startBonus?: JarvisPriceMap,
): Partial<IHighlightBadgeProperties> | null => {
    if (!isContentEnabled || (!startBonus || !startBonus.price)) {
        return null;
    }

    const tooltipConfig = getCMSConfig().offerSummaryCard.highlightBadge?.tooltip;

    const txtContent = createStartBonusText(startBonus.price);

    return txtContent ? {
        txtContent,
        ...tooltipConfig && {
            containerTooltip: {
                stdTooltipId: tooltipConfig.id,
                txtContent: tooltipConfig.text,
                stdHeadline: tooltipConfig.headline,
                business: NO_PATTERN_BUSINESS_LOGIC,
            },
        },
    } : null;
};

/**
 * Gets the subline text in the OfferSummaryCard component
 *
 * @param isContentEnabled - If tariffs are available or not
 * @param tariff - The selected tariff
 */
export const getSublineText = (isContentEnabled: boolean, tariff?: JarvisProduct) =>
    (isContentEnabled && tariff)
        ? getCMSConfig().offerSummaryCard.tariffSubline
            .replace(CmsPlaceholder.DOWNLOAD, tariff.download.toString())
            .replace(CmsPlaceholder.UPLOAD, tariff.upload.toString())
        : getCMSConfig().offerSummaryCard.notAvailable.subline;

/**
 * Gets the legal texts in the OfferSummaryCard component
 *
 * @param tariff - The selected tariff
 * @param isFriendsAndFamily - If customer has F&F
 */
export const getLegalTexts = (
    tariff: JarvisProduct | undefined,
    isFriendsAndFamily: boolean,
) => {
    if (!tariff) {
        return null;
    }

    const { friendsAndFamilyDiscount, minorityNE4Discount } = tariff;

    const friendsAndFamilyDiscountValue = isFriendsAndFamily && friendsAndFamilyDiscount ? friendsAndFamilyDiscount.price.value : 0;
    const minorityNE4DiscountValue = minorityNE4Discount ? minorityNE4Discount.price.value : 0;

    return [
        createDurationText(tariff.duration),
        createLegalDiscountText(friendsAndFamilyDiscountValue, minorityNE4DiscountValue),
    ].filter(isNotEmptyString);
};

/**
 * Creates addtitional price
 *
 * @param value Additional price value
 * @param label Addtional price description
 * @returns Additional price obaject
 */
export const createAdditionalPrice = (value: number, label: string): IOfferSummaryCardAdditionalPrice => {
    const brixPrice = Price.fromNumber(value);

    return {
        price: {
            euro: brixPrice.euro,
            cent: brixPrice.cent,
        },
        txtLabel: label,
    };
};

/**
 * Gets additional prices in the OfferSummaryCard comonent
 *
 * @param tariff - Selected tariff
 * @param isFriendsAndFamily - If customer has F&F
 * @param isYoung - If customer has young discount
 * @returns Array of addtional prices or empty array
 */
export const getAdditionalPrices = (
    tariff: JarvisProduct | undefined,
    isFriendsAndFamily: boolean,
    isYoung: boolean,
    isTVProductSelected: boolean,
    tvProduct?: JarvisTvProduct,
): Partial<IOfferSummaryCardAdditionalPrice>[] => {
    if (!tariff) {
        return [];
    }

    const additionalPrices: Partial<IOfferSummaryCardAdditionalPrice>[] = [];
    const cmsConfig = getCMSConfig().offerSummaryCard.legalText;
    const tvPrice = isTVProductSelected && tvProduct ? tvProduct.discountedPrice.value : 0;

    if (tariff.price.stairway && !isFriendsAndFamily && !tariff.youngPromoDiscount && !tariff.minorityNE4Discount) {
        additionalPrices.push(
            createAdditionalPrice(
                tariff.price.value + tvPrice,
                cmsConfig.stairway.replace(CmsPlaceholder.STAIRWAY_MONTH, (tariff.price.stairway.period + 1).toString()),
            ),
        );
    }

    if (tariff.provisionFee) {
        additionalPrices.push({
            price: Price.fromNumber(tariff.provisionFee.price.value),
            txtLabel: FeeText.PROVISION_FEE,
        });
    }

    if (tariff.routerDeliveryFee) {
        additionalPrices.push({
            price: Price.fromNumber(tariff.routerDeliveryFee.price.value),
            txtLabel: FeeText.ROUTER_DELIVERY_FEE,
        });
    }

    return additionalPrices;
};
